import React, { useState, useEffect } from "react";
import { PhotoIcon, StarIcon as StarOutline, XMarkIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";
import { Review } from "./PlaceDetail";
import useUserStore from "../store/userStore";
import ModalTemplate from "./ModalWrapper";
import SignUp from "./SignUp";
import Login from "./Login";
import { ReactComponent as EditIcon } from "../assets/icons/pencil-01.svg";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface WriteCommentProps {
  className?: string;
  placeId?: string;
  userId?: string;
  onReviewSubmitted?: (newReview: Review) => void;
}

const WriteComment: React.FC<WriteCommentProps> = ({
  className = "",
  placeId,
  userId,
  onReviewSubmitted,
}) => {
  const user = useUserStore((state) => state.user);
  const token = useUserStore((state) => state.token);

  const [name, setName] = useState(user?.fullName || user?.username || "");
  const [email, setEmail] = useState(user?.email || "");
  const [comment, setComment] = useState("");
  const [rating, setRating] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState(false);
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  const [modalType, setModalType] = useState<"login" | "signup" | null>(null);

  useEffect(() => {
    if (user) {
      setName(user.fullName || user.username || "");
      setEmail(user.email || "");
    }
  }, [user]);

  useEffect(() => {
    const previews = selectedImages.map((image) => URL.createObjectURL(image));
    setImagePreviews(previews);

    return () => previews.forEach((preview) => URL.revokeObjectURL(preview));
  }, [selectedImages]);

  const handleImageSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    const invalidFile = files.find((file) => !allowedTypes.includes(file.type) || file.size > 20 * 1024 * 1024);

    if (invalidFile) {
      setErrorMessage("فقط فایل‌های JPEG، PNG، GIF یا WebP تا حجم ۲۰ مگابایت قابل بارگذاری هستند.");
      event.target.value = "";
      return;
    }

    setSelectedImages((currentImages) => {
      const existingFiles = new Set(currentImages.map((file) => `${file.name}-${file.size}-${file.lastModified}`));
      return [...currentImages, ...files.filter((file) => !existingFiles.has(`${file.name}-${file.size}-${file.lastModified}`))];
    });
    setErrorMessage("");
    event.target.value = "";
  };

  const removeSelectedImage = (index: number) => {
    setSelectedImages((currentImages) => currentImages.filter((_, imageIndex) => imageIndex !== index));
  };

  const uploadImages = async (reviewId: string) => {
    const uploadResults = await Promise.allSettled(
      selectedImages.map(async (image) => {
        const formData = new FormData();
        formData.append("local_path", image);
        formData.append("filename", image.name);
        formData.append("review", reviewId);

        const response = await fetch(`${BASE_URL}/api/images/`, {
          method: "POST",
          headers: token
            ? { Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}` }
            : undefined,
          credentials: "include",
          body: formData,
        });

        if (!response.ok) {
          const errorBody = await response.json().catch(() => null);
          throw new Error(errorBody?.message ?? errorBody?.detail ?? "خطا در بارگذاری تصویر");
        }

        const body = await response.json();
        return body.data ?? body;
      }),
    );

    return {
      uploadedImages: uploadResults
        .filter((result): result is PromiseFulfilledResult<any> => result.status === "fulfilled")
        .map((result) => result.value),
      hasUploadError: uploadResults.some((result) => result.status === "rejected"),
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setIsMobileSheetOpen(false);
      setModalType("login");
      return;
    }

    const currentUserId = user?.userId || userId;

    if (!placeId || !currentUserId) {
      setErrorMessage("شناسه مکان یا کاربر مشخص نیست.");
      return;
    }

    if (!comment.trim()) {
      setErrorMessage("لطفاً نظر خود را بنویسید.");
      return;
    }

    if (!rating) {
      setErrorMessage("لطفاً امتیاز خود را انتخاب کنید.");
      return;
    }


    setLoading(true);
    setErrorMessage("");

    try {
      const res = await fetch(`${BASE_URL}/api/reviews/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token
            ? { Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}` }
            : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          place: placeId,
          user: currentUserId,
          rate: rating,
          comment: comment,
        }),
      });

      if (!res.ok) {
        const errorBody = await res.json().catch(() => null);
        throw new Error(
          errorBody?.error?.message ??
          errorBody?.message ??
          errorBody?.detail ??
          "خطا در ثبت دیدگاه"
        );
      }

      const responseBody = await res.json();
      const newReviewData = responseBody.data ?? responseBody;
      const backendReviewId = newReviewData.review_id || newReviewData._id;
      const reviewId = backendReviewId || Date.now().toString();
      const { uploadedImages, hasUploadError } = selectedImages.length > 0 && backendReviewId
        ? await uploadImages(backendReviewId)
        : { uploadedImages: [], hasUploadError: selectedImages.length > 0 };

      const currentUserName = user?.fullName || user?.username || "کاربر سایت";

      const newReview: Review = {
        review_id: reviewId,
        place: placeId,
        place_name: newReviewData.place_name || "",
        user: currentUserId,
        user_name: currentUserName,
        full_name: currentUserName,
        rate: rating,
        comment: comment,
        date: new Date().toISOString(),
        images: uploadedImages,
      };

      if (onReviewSubmitted) {
        onReviewSubmitted(newReview);
      }

      setSuccessMessage(true);
      setTimeout(() => setSuccessMessage(false), 3000);

      setComment("");
      setRating(0);
      setSelectedImages([]);

      if (hasUploadError) {
        setErrorMessage("نظر ثبت شد، اما بارگذاری یک یا چند تصویر انجام نشد.");
      }
    } catch (err: any) {
      console.error("❌ خطا در ثبت کامنت:", err);
      setErrorMessage(err.message || "خطا در ارسال دیدگاه.");
    } finally {
      setLoading(false);
    }
  };

  const selectMobileRating = (star: number) => {
    setRating(star);
    setErrorMessage("");
    setIsMobileSheetOpen(true);
  };

  const ratingStars = (sizeClassName: string, showNumbers = false) => (
    <div className="flex items-center justify-center gap-1.5" dir="ltr">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => setRating(star)}
          className="relative flex items-center justify-center rounded-full p-1 transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#038545]"
          aria-label={`امتیاز ${star} از ۵`}
        >
          {star <= rating ? (
            <StarSolid className={`${sizeClassName} text-[#038545]`} />
          ) : (
            <StarOutline className={`${sizeClassName} stroke-[1.25] text-gray-400`} />
          )}
          {showNumbers && (
            <span
              className={`pointer-events-none absolute translate-y-[2px] text-xs font-myYekanDemibold ${star <= rating ? "text-white" : "text-gray-400"
                }`}
            >
              {star.toLocaleString("fa-IR")}
            </span>
          )}
        </button>
      ))}
    </div>
  );

  const renderImagePicker = (inputId: string, compact = false) => (
    <div className={compact ? "mt-3" : ""}>
      <input
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        multiple
        className="sr-only"
        onChange={handleImageSelection}
      />
      <label
        htmlFor={inputId}
        className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[0.72rem] font-myYekanMedium text-[#068646] transition-colors hover:border-[#038545] hover:bg-[#f3fefa] focus-within:ring-2 focus-within:ring-[#038545]"
      >
        <PhotoIcon className="h-4 w-4" aria-hidden="true" />
        افزودن تصویر
      </label>
      {imagePreviews.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" aria-label="تصاویر انتخاب‌شده">
          {imagePreviews.map((preview, index) => (
            <div key={preview} className="relative h-16 w-16 overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
              <img src={preview} alt={`پیش‌نمایش تصویر ${index + 1}`} className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => removeSelectedImage(index)}
                className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/65 text-white transition-colors hover:bg-black"
                aria-label={`حذف تصویر ${index + 1}`}
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <section className={`mt-4 rounded-xl bg-[#FAFAFA] p-4 tablet:hidden ${className}`}>
        <h2 className="text-center text-lg font-myYekanDemibold leading-[1.7] text-[#222222]">
          به این مکان امتیاز دهید
        </h2>
        <div className="mt-3">
          <div className="flex items-center justify-center gap-1.5" dir="ltr">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => selectMobileRating(star)}
                className="relative flex h-11 w-11 items-center justify-center rounded-full transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#038545]"
                aria-label={`امتیاز ${star} از ۵`}
              >
                {star <= rating ? (
                  <StarSolid className="h-10 w-10 text-[#038545]" />
                ) : (
                  <StarOutline className="h-10 w-10 stroke-[1] text-gray-400" />
                )}
                <span
                  className={`pointer-events-none absolute translate-y-[2px] text-xs font-myYekanDemibold ${star <= rating ? "text-white" : "text-gray-400"
                    }`}
                >
                  {star.toLocaleString("fa-IR")}
                </span>
              </button>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setIsMobileSheetOpen(true)}
          className="mt-4 flex min-h-[48px] w-full items-center rounded-xl border border-gray-300 bg-white px-4 text-right text-md font-myYekanRegular text-gray-500 transition-colors hover:border-[#038545] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#038545]"
        >
          نظرتان را درباره این مکان بنویسید
        </button>
      </section>

      <form onSubmit={handleSubmit} className={`mt-[16px] hidden w-full tablet:block ${className}`}>

        {errorMessage && (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-sm">
            <span className="text-[0.77rem] font-myYekanMedium leading-[1.8]">
              {errorMessage}
            </span>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3 text-primary-600 shadow-sm">
            <span className="text-[0.77rem] font-myYekanMedium leading-[1.8]">
              دیدگاه شما با موفقیت ثبت شد
            </span>
          </div>
        )}
        <div className="relative mb-4 mt-2 w-full">
          <textarea
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              if (errorMessage) setErrorMessage("");
            }}
            rows={5}
            placeholder="درباره تجربه خود بنویسید..."
            className="h-[140px] w-full resize-none rounded-lg border border-gray-200 bg-white px-4 py-4 text-[0.77rem] font-myYekanRegular leading-[2] text-gray-700 placeholder:text-gray-400 focus:border-[#038545] focus:outline-none focus:ring-2 focus:ring-[#038545]/20"
            dir="rtl"
          />

          <div className="absolute bottom-3 left-3 flex items-center gap-4">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className="focus:outline-none"
                >
                  {star <= rating ? (
                    <StarSolid className="h-6 w-6 text-[#038545]" />
                  ) : (
                    <StarOutline className="h-6 w-6 text-gray-400 hover:text-[#038545]" />
                  )}
                </button>
              ))}
            </div>
            <button
              type="submit"
              className="flex items-center gap-2 rounded-xl border border-[#038545] px-4 py-2 text-[0.77rem] font-myYekanMedium text-[#068646] hover:bg-[#f3fefa] disabled:opacity-50"
              disabled={loading}
            >
              {loading ? "در حال ارسال..." : "ارسال نظر"}
              <div className="group">
                <EditIcon className="w-4 h-4 text-[#068646]" />
              </div>           </button>
          </div>
        </div>
        {renderImagePicker("desktop-review-image-upload")}

      </form>

      <ModalTemplate
        showModal={isMobileSheetOpen}
        mobileBottomSheet
        onClose={() => setIsMobileSheetOpen(false)}
        mainComponent={
          <form onSubmit={handleSubmit} className="w-full px-4 pb-4 pt-1" dir="rtl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-[1.05rem] font-myYekanDemibold text-[#222222]">
                نظر شما درباره این مکان
              </h2>
              <button
                type="button"
                onClick={() => setIsMobileSheetOpen(false)}
                className="rounded-full p-1.5 text-gray-500 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#038545]"
                aria-label="بستن"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              {/* <span className="w-8" aria-hidden="true" /> */}
            </div>

            {errorMessage && (
              <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[0.75rem] font-myYekanMedium text-red-700">
                {errorMessage}
              </div>
            )}
            {successMessage && (
              <div className="mb-3 rounded-xl border border-primary-200 bg-primary-50 px-3 py-2 text-[0.75rem] font-myYekanMedium text-primary-600">
                دیدگاه شما با موفقیت ثبت شد
              </div>
            )}

            <div>
              {/* <p className="mb-2 text-center text-[0.82rem] font-myYekanMedium text-[#222222]">
                به این مکان امتیاز دهید
              </p> */}
              {ratingStars("h-8 w-8", true)}
              <textarea
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                  if (errorMessage) setErrorMessage("");
                }}
                rows={5}
                placeholder="نظرتان را درباره این مکان بنویسید"
                className="mt-4 h-28 w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-3 text-[0.8rem] font-myYekanRegular leading-[1.9] text-gray-700 placeholder:text-gray-400 focus:border-[#038545] focus:outline-none focus:ring-2 focus:ring-[#038545]/20"
              />
              {renderImagePicker("mobile-review-image-upload", true)}
            </div>
            <button
              type="submit"
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-[#038545] px-4 py-3 text-[0.82rem] font-myYekanDemibold text-white transition-colors hover:bg-[#068646] disabled:cursor-not-allowed disabled:opacity-50"
              disabled={loading}
            >
              {loading ? "در حال ارسال..." : "ارسال نظر"}
              <EditIcon className="h-4 w-4 text-white" />
            </button>
          </form>
        }
      />

      {modalType && (
        <ModalTemplate
          showModal={true}
          mobileBottomSheet
          className="!w-full !bg-white !px-0 !py-0 tablet:!w-[340px] tablet:!rounded-3xl tablet:!border tablet:!border-gray-200 tablet:!shadow-main desktop:!w-[440px]"
          onClose={() => setModalType(null)}
          mainComponent={
            modalType === "login" ? (
              <Login
                onClose={() => setModalType(null)}
                switchToSignup={() => setModalType("signup")}
              />
            ) : (
              <SignUp
                onClose={() => setModalType(null)}
                switchToLogin={() => setModalType("login")}
              />
            )
          }
        />
      )}
    </>
  );
};

export default WriteComment;
